'use client';

import { defaultLocale, defaultMarket, IMedia, IRoute } from '@websolutespa/bom-core';
import { createGenericContext, useLayout, usePage } from '@websolutespa/bom-mixer-hooks';
import { getPublicUrl, IAlternate, IBrand, IMeta, IMetaData, OpenGraphType, resolveHref, StructuredDataType, TwitterCardType } from '@websolutespa/bom-mixer-models';
import { convert as htmlToText } from 'html-to-text';
import { useRouter } from 'next/compat/router';
import React, { useMemo } from 'react';
import { ContactPoint, Organization, PostalAddress } from 'schema-dts';

export type IMetaDataContext = IMetaData;

const [useMetaData, MetaDataContextProvider] = createGenericContext<IMetaDataContext>();

function MetaDataProvider({ children, metaData }: {
  children?: React.ReactNode, metaData: {
    brand: {
      name: string;
      logo: string;
      businessName?: string;
      appName?: string;
      url?: string;
      email?: string;
      telephone?: string;
      zipCode?: string;
      address?: string;
      city?: string;
      provinceCode?: string;
      countryName?: string;
      countryCode?: string;
      twitterName?: string;
      social?: Record<string, string>;
    },
    structuredData?: StructuredDataType;
    openGraph?: OpenGraphType;
    twitterCard?: TwitterCardType;
  }
}) {
  const router = useRouter();
  const layout = useLayout();
  const page = usePage();
  const brand: IBrand = {
    ...metaData.brand,
    logo: resolveHref(metaData.brand.logo),
    url: getPublicUrl(), // layout.topLevelHrefs.homepage,
    businessName: metaData.brand.businessName || metaData.brand.name,
    appName: metaData.brand.appName || metaData.brand.name,
  };
  const title = htmlToText(page.meta?.title || page.title || '');
  const description = htmlToText(page.meta?.description || page.abstract || '');
  const media = ((page.meta as { image: IMedia } | undefined)?.image || page.media);
  const image = media ? resolveHref(media.src || media.url) : brand.logo;
  let canonical = resolveHref(page.href);
  const asPath = router?.asPath || '';
  const queryIndex = asPath.indexOf('?');
  const query = queryIndex !== -1 ? asPath.substring(queryIndex) : '';
  if (query.match(/^\?pagination\[page\]=(\d+)$/) && query !== '?pagination[page]=1') {
    canonical = `${canonical}${query}`;
    // title = `${title} - ${query.replace('?pagination[page]=','')}`;
  }
  const url = `${canonical}${query}`;
  const alternates = useMemo(() => {
    const alternates: IAlternate[] = [];
    (page.alternates || []).forEach((alternate: IRoute) => {
      const market = layout.markets.find(x => x.id === alternate.market);
      if (market) {
        if (market.countries && market.countries.length > 0) {
          market.countries.forEach((country: any) => {
            // country: string | { alpha2Code: string };
            const alpha2Code = typeof country === 'string' ? country : country.alpha2Code;
            alternates.push({
              id: `${alternate.market}-${alternate.locale}-${alpha2Code}`,
              href: resolveHref(alternate.id),
              hrefLang: `${alternate.locale}-${alpha2Code}`,
            });
          });
        } else {
          alternates.push({
            id: `${alternate.market}-${alternate.locale}`,
            href: resolveHref(alternate.id),
            hrefLang: alternate.locale,
          });
        }
      }
    });
    return alternates;
  }, [layout, page]);
  const xDefault = useMemo(() => {
    const xDefaultMarket = layout.markets.find(x => x.isDefault)?.id || defaultMarket;
    const xDefaultLocale = layout.locales.find(x => x.isDefault)?.id || defaultLocale;
    const alternate = page.alternates.find(x => x.market === xDefaultMarket && x.locale === xDefaultLocale);
    if (alternate) {
      return {
        id: `${alternate.market}-${alternate.locale}`,
        href: resolveHref(alternate.id),
        hrefLang: alternate.locale,
      };
    }
    return undefined;
  }, [layout, page]);
  const meta: IMeta = {
    title: title,
    description: description,
    image: image,
    keywords: page.meta?.keywords,
    robots: page.meta?.robots,
    slug: page.href,
    url,
    canonical,
    alternates,
    xDefault,
  };
  const contactPoint: ContactPoint = {
    '@type': 'ContactPoint',
    telephone: brand.telephone,
    email: brand.email,
    contactType: 'customer service',
    areaServed: layout.locales.map(x => x.id),
    availableLanguage: layout.locales.map(x => x.id),
  };
  const postalAddress: PostalAddress = {
    '@type': 'PostalAddress',
    streetAddress: brand.address,
    addressLocality: brand.city,
    postalCode: brand.zipCode,
    addressRegion: brand.provinceCode,
    addressCountry: brand.countryCode,
  };
  const organization: Organization = {
    '@type': 'Organization',
    name: brand.businessName,
    logo: {
      '@type': 'ImageObject',
      url: brand.logo,
    },
    url: brand.url,
    /*
    description,
    founder,
    foundingDate,
    numberOfEmployees,
    */
    /*
    foundingDate: '2020-01-01',
    numberOfEmployees: {
      '@type': 'QuantitativeValue',
      value: 150,
    },
    */
  };
  const hasContact = Boolean(brand.email || brand.telephone);
  if (hasContact) {
    organization.contactPoint = contactPoint;
  }
  const hasAddress = Boolean(brand.address || brand.city || brand.zipCode || brand.provinceCode || brand.countryCode);
  if (hasAddress) {
    organization.address = postalAddress;
  }
  if (brand.social) {
    organization.sameAs = Object.values(brand.social);
  }
  const structuredData: StructuredDataType = metaData.structuredData || {
    '@type': 'WebPage',
    name: title,
    url: url,
    description: description,
    publisher: organization,
  };
  const openGraph: OpenGraphType = metaData.openGraph || {
    type: 'website',
    title: title,
    description: description,
    url: url,
    siteName: brand.name,
    image: image,
    locale: layout.locale,
  };
  const twitterCard: TwitterCardType | undefined = metaData.twitterCard || (
    brand.twitterName ? {
      card: 'summary_large_image',
      title: title,
      description: description,
      image: image,
      imageAlt: brand.businessName,
      site: brand.twitterName,
      creator: brand.twitterName,
    } : undefined
  );
  const value: IMetaData = {
    layout,
    page,
    brand,
    contactPoint,
    meta,
    openGraph,
    organization,
    postalAddress,
    structuredData,
    twitterCard,
  };
  return (
    <MetaDataContextProvider value={value}>
      {children}
    </MetaDataContextProvider>
  );
}

export { MetaDataProvider, useMetaData };

